import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import type { SmartCategory } from '@/types/contact';
import { GraphService } from '@/services/graphService';

export const useUserSettings = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [enableCategories, setEnableCategories] = useState(false);
  const [autoRunCategories, setAutoRunCategories] = useState(false);
  const [categories, setCategories] = useState<SmartCategory[]>([]);
  const [activeCategories, setActiveCategories] = useState<string[]>([]);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setIsLoading(true);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.warn('[Smart Categories] No user logged in');
        return;
      }

      // Load settings
      const { data: settings, error: settingsError } = await supabase
        .from('user_settings')
        .select('enable_categories, auto_run_categories')
        .eq('user_id', user.id)
        .maybeSingle();

      if (settingsError) throw settingsError;

      if (settings) {
        setEnableCategories(settings.enable_categories ?? false);
        setAutoRunCategories(settings.auto_run_categories ?? false);
      }

      // Load categories
      const { data: categoriesData, error: categoriesError } = await supabase
        .from('smart_categories')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (categoriesError) throw categoriesError;

      const typedCategories: SmartCategory[] = (categoriesData || []).map(cat => ({
        id: cat.id,
        user_id: cat.user_id,
        name: cat.name,
        description: cat.description,
        color: cat.color as any,
        tags: cat.tags,
        is_active: cat.is_active || false,
        created_at: cat.created_at,
        updated_at: cat.updated_at
      }));

      setCategories(typedCategories);
      
      const activeCategoryIds = typedCategories
        .filter(c => c.is_active)
        .map(c => c.id);
      setActiveCategories(activeCategoryIds);

    } catch (error) {
      console.error('[Smart Categories] Failed to load settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to load Smart Categories settings',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const toggleCategories = async (enabled: boolean) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('user_settings')
        .upsert({
          user_id: user.id,
          enable_categories: enabled,
          auto_run_categories: enabled ? autoRunCategories : false,
        }, {
          onConflict: 'user_id'
        });

      if (error) throw error;

      setEnableCategories(enabled);
      if (!enabled) {
        setAutoRunCategories(false);
      }
      
      toast({
        title: enabled ? 'Smart Categories Enabled' : 'Smart Categories Disabled',
        description: enabled 
          ? 'AI categorization will be available for your contacts'
          : 'Smart Categories has been turned off',
      });
    } catch (error) {
      console.error('[Smart Categories] Failed to toggle:', error);
      toast({
        title: 'Error',
        description: 'Failed to update settings',
        variant: 'destructive',
      });
    }
  };

  const toggleAutoRun = async (enabled: boolean) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      if (enabled && !enableCategories) {
        toast({
          title: 'Cannot enable Auto-run',
          description: 'Please enable Smart Categories first',
          variant: 'destructive',
        });
        return;
      }

      const { error } = await supabase
        .from('user_settings')
        .upsert({
          user_id: user.id,
          auto_run_categories: enabled,
        }, {
          onConflict: 'user_id'
        });

      if (error) throw error;

      setAutoRunCategories(enabled);
      
      toast({
        title: enabled ? 'Auto-run Enabled' : 'Auto-run Disabled',
        description: enabled 
          ? 'Smart Categories will run automatically when AI-enhancing contacts'
          : 'You will need to manually run Smart Categories',
      });
    } catch (error) {
      console.error('[Smart Categories] Failed to toggle auto-run:', error);
      toast({
        title: 'Error',
        description: 'Failed to update settings',
        variant: 'destructive',
      });
    }
  };

  const toggleCategoryActive = async (id: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const category = categories.find(c => c.id === id);
      if (!category) return false;

      // Prevent activation if no tags
      if (!category.is_active && category.tags.length === 0) {
        toast({
          title: 'Cannot activate',
          description: 'Please add at least one tag before activating this category',
          variant: 'destructive',
        });
        return false;
      }

      const newActiveState = !category.is_active;

      // If activating, ensure category exists in Outlook FIRST
      if (newActiveState) {
        try {
          console.log(`[Smart Categories] Creating category in Outlook: ${category.name}`);
          await GraphService.ensureCategory(category.name, category.color);
          console.log(`[Smart Categories] ✅ Category created/verified in Outlook`);
        } catch (outlookError: any) {
          console.error('[Smart Categories] Failed to create in Outlook:', outlookError);
          toast({
            title: 'Outlook sync failed',
            description: outlookError.message || 'Could not create category in Outlook. Please try again.',
            variant: 'destructive',
          });
          return false;
        }
      }

      const { error } = await supabase
        .from('smart_categories')
        .update({ is_active: newActiveState })
        .eq('id', id);

      if (error) throw error;

      setCategories(prev => prev.map(c => 
        c.id === id ? { ...c, is_active: newActiveState } : c
      ));

      setActiveCategories(prev => 
        newActiveState 
          ? [...prev, id]
          : prev.filter(cid => cid !== id)
      );

      toast({
        title: newActiveState ? 'Category Activated' : 'Category Deactivated',
        description: newActiveState 
          ? 'Category created in Outlook and ready for AI classification'
          : 'This category is now inactive',
        duration: 3000,
      });

      return true;
    } catch (error) {
      console.error('[Smart Categories] Failed to toggle active:', error);
      toast({
        title: 'Error',
        description: 'Failed to update category',
        variant: 'destructive',
      });
      return false;
    }
  };

  return {
    isLoading,
    enableCategories,
    autoRunCategories,
    categories,
    activeCategories,
    toggleCategories,
    toggleAutoRun,
    toggleCategoryActive,
    refresh: loadSettings,
  };
};
